import { useState, useMemo } from 'react';
import { items } from '../data/items';
import { ITEM_RARITIES } from '../types';
import type { Item, ItemRarity } from '../types';
import { ItemCard } from '../components/ItemCard';
import { CardListPage, type CardListPageConfig } from '../components/CardListPage';
import { useImport } from '../context/useImport';
import './ItemsPage.css';

// Extract unique base types from items (e.g., "Weapon (Martial)" -> "Weapon")
function getBaseType(type: string): string {
  const parenIndex = type.indexOf('(');
  if (parenIndex > 0) {
    return type.substring(0, parenIndex).trim();
  }
  return type;
}

// Wrapper components to adapt entity prop to item prop
function ItemListCard({ entity, isSelected, onToggle }: { entity: Item; isSelected: boolean; onToggle: (id: string) => void }) {
  return <ItemCard item={entity} isSelected={isSelected} onToggle={onToggle} />;
}

export function ItemsPage() {
  const [selectedType, setSelectedType] = useState<string>('Any');
  const [selectedRarity, setSelectedRarity] = useState<ItemRarity | 'Any'>('Any');
  const [importedOnly, setImportedOnly] = useState(false);

  const {
    customSpells,
    customItems,
    customClasses,
    customFeats,
    multiTabImportState,
    importMultiTab,
    importMultipleUrls,
    clearMultiTabImportStatus,
    clearCustomSpells,
    clearCustomItems,
    clearCustomClasses,
    clearCustomFeats,
    combinedTemplateUrl,
  } = useImport();

  // Combine base items with custom items
  const allItems = useMemo(() => [...items, ...customItems], [customItems]);
  const visibleItems = useMemo(
    () => (importedOnly ? customItems : allItems),
    [importedOnly, customItems, allItems]
  );

  // Recalculate types including custom items
  const allItemTypes = useMemo(() => {
    return [...new Set(visibleItems.map(item => getBaseType(item.type)))].sort();
  }, [visibleItems]);

  const filteredItems = useMemo(() => {
    return visibleItems
      .filter((item) => {
        // Type filter - match base type
        if (selectedType !== 'Any' && getBaseType(item.type) !== selectedType) {
          return false;
        }
        // Rarity filter
        if (selectedRarity !== 'Any' && item.rarity !== selectedRarity) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedType, selectedRarity, visibleItems]);

  const renderFilters = () => (
    <>
      <div className="filter-row">
        <div className="filter-group">
          <label>TYPE:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="Any">Any</option>
            {allItemTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>RARITY:</label>
          <select
            value={selectedRarity}
            onChange={(e) => setSelectedRarity(e.target.value as ItemRarity | 'Any')}
            className="filter-select"
          >
            <option value="Any">Any</option>
            {ITEM_RARITIES.map((rarity) => (
              <option key={rarity} value={rarity}>
                {rarity}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group imported-only-group">
          <label htmlFor="items-imported-only" className="imported-only-label">
            <input
              id="items-imported-only"
              type="checkbox"
              checked={importedOnly}
              onChange={(e) => setImportedOnly(e.target.checked)}
              className="imported-only-checkbox"
            />
            <span>IMPORTED ONLY</span>
          </label>
        </div>
      </div>
    </>
  );

  const config: CardListPageConfig<Item> = {
    variant: 'items',
    pageClassName: 'items-page',
    listClassName: 'items-list',
    documentTitle: 'Magic Item Cards',
    searchPlaceholder: 'Search items...',
    emptyState: {
      icon: '⚔️',
      title: 'Magic Item Cards',
      description: 'Select items from the list to preview them here',
      instructions: [
        '1. Use the filters to find items by type or rarity',
        '2. Click on items to add them to your selection',
        '3. Download your cards when ready to print',
      ],
    },
    import: {
      onImport: importMultiTab,
      onImportMultiple: importMultipleUrls,
      state: multiTabImportState,
      onClearStatus: clearMultiTabImportStatus,
      spellsCount: customSpells.length,
      itemsCount: customItems.length,
      classesCount: customClasses.length,
      featsCount: customFeats.length,
      onClearSpells: clearCustomSpells,
      onClearItems: clearCustomItems,
      onClearClasses: clearCustomClasses,
      onClearFeats: clearCustomFeats,
      combinedTemplateUrl,
    },
    ListCard: ItemListCard,
    renderFilters,
  };

  return (
    <CardListPage
      allEntities={visibleItems}
      filteredEntities={filteredItems}
      config={config}
    />
  );
}
